import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import {
    debounce,
    FormMessage,
    InputValue,
    SearchInput,
    Stack,
    StackItem,
    Status,
    Typography,
} from '@optimus-web/core';
import { OptimusTextKey, useLocalization } from '@optimus-web/localization';
import { TreeContextProvider } from './context';
import { OptionsList } from './style';
import { TreeOptionEntry } from './TreeOptionEntry';
import { TreeOptionsGroup } from './TreeOptionsGroup';
import { TreeProps } from './types';
import { useOptions } from './useOptions';
import { useToggleExpand } from './useToggleExpand';
import {
    combineKeydownEvents,
    getTotalNotDisabledOptionsCount,
    getTotalOptionsCount,
    handleKeyboardClick,
    handleKeyboardFocusControl,
    selectAllFromGroup,
} from './utils';

const getInitialLocalValue = (value: string | string[] | null | undefined) => {
    if (Array.isArray(value)) {
        return value;
    }

    return value ? [value] : [];
};

export const Tree = forwardRef(
    (
        {
            id,
            error,
            value,
            options,
            title,
            onChange,
            showSelectAllToggle,
            selectAllLabel,
            showCount,
            multiple,
            defaultExpandAll,
            defaultExpandedIds,
            size = 'medium',
            showSearchBar,
            showDividers,
            searchDebounceTime,
            searchValue,
            onSearchChange,
            selectionStrategy = 'children-only',
        }: TreeProps,
        forwardedRef
    ) => {
        const [localValue, setLocalValue] = useState(getInitialLocalValue(value));
        const [searchQuery, setSearchQuery] = useState<InputValue>('');
        const ref = useRef<HTMLUListElement>(null);
        const { t } = useLocalization();

        const { ref: toggleRef, onToggleExpand } = useToggleExpand();

        useEffect(() => {
            setLocalValue(getInitialLocalValue(value));
        }, [value]);

        const handleChange = useCallback(
            (newValue: string[]) => {
                setLocalValue(newValue);
                if (multiple) {
                    onChange?.(newValue);
                } else {
                    onChange?.(newValue[0] || null);
                }
            },
            [multiple, onChange]
        );

        const handleKeyDown = combineKeydownEvents(handleKeyboardClick, handleKeyboardFocusControl(ref.current));

        const isUncontrolledSearch = searchValue === undefined;
        const effectiveSearchQuery = isUncontrolledSearch ? searchQuery : searchValue;

        const mappedOptions = useOptions({
            options,
            searchQuery,
            value: localValue,
            multiple,
            handleChange,
            handleKeyDown,
            selectionStrategy,
        });

        const handleChangeSearchQuery = useMemo(() => {
            const internalChange = (v: InputValue) => {
                if (isUncontrolledSearch) {
                    setSearchQuery(v);
                }
                onSearchChange?.(v);
            };

            return searchDebounceTime != null ? debounce(internalChange, searchDebounceTime) : internalChange;
        }, [searchDebounceTime, onSearchChange, isUncontrolledSearch]);

        const totalOptions = getTotalOptionsCount(mappedOptions, selectionStrategy);
        const totalNotDisabledOptions = getTotalNotDisabledOptionsCount(mappedOptions, selectionStrategy);

        const allSelected = [totalOptions, totalNotDisabledOptions].includes(localValue.length);

        useImperativeHandle(
            forwardedRef,
            () => ({
                onToggleExpand: (e: boolean) => {
                    onToggleExpand(e);
                },
            }),
            [onToggleExpand]
        );

        const shouldShowSelectAllToggle = showSelectAllToggle && multiple && totalNotDisabledOptions > 0;

        const contextValue = useMemo(() => ({ showCount, multiple, size }), [showCount, multiple, size]);

        const defaultExpandedIdsSet = useMemo(() => new Set(defaultExpandedIds || []), [defaultExpandedIds]);

        return (
            <TreeContextProvider value={contextValue}>
                <Stack vertical spacing="300">
                    <Stack vertical spacing="100">
                        <Stack distribution="space-between" itemAlignment="center" spacing="300">
                            {title ? (
                                <StackItem>
                                    <Typography textStyle="titleSmallStrong" ellipsis as="div">
                                        {title}
                                    </Typography>
                                </StackItem>
                            ) : null}

                            {showSearchBar ? (
                                <StackItem grow>
                                    <SearchInput
                                        onSearch={() => null}
                                        onChange={(_, v) => handleChangeSearchQuery(v)}
                                        size="medium"
                                        name={`${id}-search`}
                                        clearable
                                        onClear={() => handleChangeSearchQuery('')}
                                    />
                                </StackItem>
                            ) : null}
                        </Stack>
                        {error ? (
                            <FormMessage status={Status.Error} id={`${id}-error`} data-test-message-error role="alert">
                                <Stack alignment="start">{error}</Stack>
                            </FormMessage>
                        ) : null}
                    </Stack>

                    <OptionsList ref={ref} aria-expanded>
                        {shouldShowSelectAllToggle ? (
                            <TreeOptionEntry
                                id={`${id}-select-all`}
                                label={selectAllLabel || t(OptimusTextKey.SelectAll)}
                                optionsCount={totalOptions}
                                options={mappedOptions}
                                onChange={() =>
                                    handleChange(
                                        allSelected
                                            ? []
                                            : mappedOptions.flatMap((o) => selectAllFromGroup(o, selectionStrategy)) ||
                                                  []
                                    )
                                }
                                onKeyDown={handleKeyDown}
                                indeterminate={!allSelected && localValue.length > 0}
                                checked={allSelected}
                                data-test-tree-select-all
                                selectionStrategy={selectionStrategy}
                            />
                        ) : null}

                        {totalOptions > 0 ? (
                            mappedOptions.map((props, index) => (
                                <TreeOptionsGroup
                                    ref={toggleRef(index)}
                                    key={props.id}
                                    {...props}
                                    defaultExpandedIds={defaultExpandedIdsSet}
                                    defaultExpanded={defaultExpandAll}
                                    showDivider={showDividers}
                                    isSearching={effectiveSearchQuery !== ''}
                                    selectionStrategy={selectionStrategy}
                                />
                            ))
                        ) : (
                            <Typography textStyle="bodyMedium" align="left" as="div">
                                {t(OptimusTextKey.NoResultsForSearch)}
                            </Typography>
                        )}
                    </OptionsList>
                </Stack>
            </TreeContextProvider>
        );
    }
);
